import { Fragment, memo, useCallback, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { IconButton, ListRow, WithTooltip } from 'basic-components';
import { Exit, History, Pin } from 'basic-components/icons';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import TimeService from 'dashboard-services/services/TimeService';
import { StatusIcon } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import { statusesQueries } from 'queries/modules/statuses';
import { customInsightSelectors } from 'selectors/modules/insight/custom';

import { When } from 'react-if';

import './Row.scss';

const { useFromCustomState } = customInsightSelectors
const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};

// eslint-disable-next-line
const Row = ({ name = "", status = undefined, startTime = "", style = EMPTY_OBJ, onHistory = undefined, setExpanded, isScrolling, version = "", rootId = "", warnStatuses = EMPTY_ARRAY, scrollCont, jobId, withOnClick = true, isPinned = false, payload = EMPTY_OBJ, type = undefined, togglePinned = undefined, withLazy = true, withStatusesLink = true, componentStatuses = EMPTY_ARRAY }) => {
  const dispatch = useDispatch(),
        editActive = useFromCustomState("editActive"),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        parsedTime = useMemo(() => TimeService.getUserFormattedDateTime(startTime, definedTime), [definedTime, startTime]),
        goToDetails = useCallback(() => {
          batch(() => {
            dispatch(statusesActions.addToHistory())
            dispatch(statusesActions.changeValue(statusesActions.MODES.DETAILS, "mode"))
            dispatch(statusesActions.changeValue(jobId, "jobId"))
          })
        }, [dispatch, jobId]),
        getNonETLId = useCallback(() => statusesActions.getNonETLId(payload, componentStatuses), [payload, componentStatuses]),
        openItemRaw = statusesQueries.useOpenBasedOnStatusType({ jobId, param: type === StatusesEnums.STATUS_TYPE.ETL ? (payload?.groupName || name) : getNonETLId(), type }),
        openItem = useCallback(e => {
          e.preventDefault()
          e.stopPropagation()
          openItemRaw()
        }, [openItemRaw]),
        renderContent = useCallback(() => (
          <Fragment>
            <div className="statuses__see-all__content__body__list__row__status">
              <StatusIcon
                  status={status}
                  warnStatuses={warnStatuses}
              />
            </div>
            <div className="statuses__see-all__content__body__list__row__date">
              {parsedTime.split(" ")[0]}
            </div>
            <div className="statuses__see-all__content__body__list__row__time">
              {parsedTime.split(" ")[1]}
            </div>
            <div className="statuses__see-all__content__body__list__row__name">
              {name + (version ? " v." + version : "")}
            </div>
            <div className="statuses__see-all__content__body__list__row__icons">
              <When condition={onHistory !== undefined}>
                {() => (
                  <WithTooltip
                      data-place="top"
                      short
                      text="Last runs"
                  >
                    <IconButton
                        className="statuses__see-all__content__body__list__row__icons__history"
                        onClick={onHistory}
                    >
                      <History
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </IconButton>
                  </WithTooltip>
                )}
              </When>
              <When condition={togglePinned !== undefined && (editActive || isPinned)}>
                {() => (
                  <WithTooltip
                      data-place="left"
                      text={!editActive && "This status has been pinned by the Insight editor"}
                  >
                    <div
                        className="statuses__see-all__content__body__list__row__icons__pin"
                        onClick={() => editActive && togglePinned(rootId)}
                    >
                      <Pin
                          bgColor={isPinned ? "blue-bright" : "transparent"}
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </div>
                  </WithTooltip>
                )}
              </When>
              <When condition={withStatusesLink}>
                {() => (
                  <WithTooltip
                      data-place="top"
                      short
                      text="Open"
                  >
                    <IconButton
                        className="statuses__see-all__content__body__list__row__icons__exit"
                        onClick={openItem}
                    >
                      <Exit
                          color="blue-bright"
                          height={16}
                          width={16}
                      />
                    </IconButton>
                  </WithTooltip>
                )}
              </When>
            </div>
          </Fragment>
        ), [editActive, isPinned, name, onHistory, openItem, parsedTime, rootId, status, togglePinned, version, warnStatuses, withStatusesLink])
  if(withLazy) {
    return (
      <ListRow 
          className="statuses__see-all__content__body__list__row"
          height={32}
          onClick={withOnClick ? goToDetails : undefined}
          scrollContRef={scrollCont}
      >
        {renderContent()}
      </ListRow>
    )
  }
  return (
    <div 
        className="statuses__see-all__content__body__list__row"
        onClick={withOnClick ? goToDetails : undefined}
    >
      {renderContent()}
    </div>
  )
}

Row.propTypes = {
  componentStatuses: PropTypes.array,
  isPinned: PropTypes.bool,
  name: PropTypes.string,
  onHistory: PropTypes.func,
  payload: PropTypes.object,
  rootId: PropTypes.string,
  startTime: PropTypes.string,
  status: PropTypes.string,
  style: PropTypes.object,
  togglePinned: PropTypes.func,
  type: PropTypes.string,
  version: PropTypes.string,
  warnStatuses: PropTypes.array,
  withLazy: PropTypes.bool,
  withOnClick: PropTypes.bool,
  withStatusesLink: PropTypes.bool
}

export default memo(Row);